import React, {MouseEvent, ComponentPropsWithRef, memo, useEffect, useMemo } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { fluidM } from './fluidFonts'
import { IconProps, useTheme } from '@mui/material'
import { Icons } from '../../../assets/icons'
import { isMobilePortrait } from '../../../utils'
import { BinCounter, BinRefs, BinsStore } from '../lib/types'
import { useBinContext } from './binsWrapper'
import { isArabic, useTranslation } from '../../../i18n/useTranslation'
import { useCtxParams } from '../../../context/useCommonCtx'

type Props = ComponentPropsWithRef<'div'> & {
    binRef: BinRefs
    binCounter: BinCounter,
    childBinRefs?: BinRefs[] // FIMXE redundant with children => render props? Context? hook?
    children?: React.ReactNode
    text?: string
    style?: React.CSSProperties
    hideEmpty?: boolean
    arrowType?: 'left' | 'right' | 'up' | 'down',
    onHoverBin?: BinsStore['onHoverBin']
}

const iconProps: Pick<IconProps, 'style' | 'fontSize'> = {
    style: {
        alignSelf: 'center',
        margin: '-0.35em',
    },
    fontSize: isMobilePortrait() ? 'medium' : 'large',
}

const EmptyChildBinRefs: BinRefs[] = [];
export const RankingBin = memo((p: Props) => {
    const t = useTranslation();
    const {
        text = t(p.binRef),
        binRef,
        arrowType,
        childBinRefs = EmptyChildBinRefs,
        hideEmpty = false,
        onHoverBin,
    } = p
    const muiTheme = useTheme()
    const baseColor = muiTheme.palette.primary.main
    const params = useCtxParams();

    const { isOver, over, setNodeRef } = useDroppable({
        id: binRef,
        data: { sourceContainerId: binRef },
    })
    const isOverFullBin = useMemo(() => {
        const isOverFullBin = isOver || childBinRefs?.includes(over?.id as unknown as BinRefs);
        console.debug('==== isOverFullBin', binRef, { isOverFullBin, isOver, overId: over?.id, childBinRefs })
        return isOverFullBin;
    }, [binRef, childBinRefs, isOver, over]);

    useEffect(() => {
        onHoverBin?.(binRef, isOverFullBin)
    }, [isOverFullBin, binRef]);

    const { selectedBinRef, onSelectBin } = useBinContext()
    const selected = selectedBinRef === binRef;
    const isSelectedFullBin = useMemo(() => {
        return selectedBinRef === binRef || childBinRefs.includes(selectedBinRef as BinRefs);
    }, [binRef, childBinRefs, selectedBinRef]);


    const maxQuantity = p.binCounter.max ?? 0
    const quantity = p.binCounter.stms?.length ?? 0

    const hasMaxQuantity = !!maxQuantity
    const qtyStr = hasMaxQuantity ? `${quantity}/${maxQuantity}` : quantity
    const exceeding = hasMaxQuantity && quantity > maxQuantity
    const fulfilled = hasMaxQuantity ? quantity === maxQuantity : quantity === 0

    // const size = 'pt-5 p-2 gap-2'
    const size = 'p-1'
    const negativeMargin = '-mx-1 -mb-1'
    const fullClass = `${size} flex-1 flex flex-col justify-around text-center border-solid border rounded ${
        isOverFullBin ? 'opacity-50' : 'opacity-100'
    }`
    // muiTheme.palette.grey[600]
    const baseTextStyle = {
        color: (selected || isOverFullBin)
            ? muiTheme.palette.primary.contrastText
            : muiTheme.palette.text.primary,
    }
    const baseTextClass = ``
    // const baseTextClass = `text-shadow-sm`
    // FIXME previously, the red or green was not really visible with the primary color of the theme -> contrastText when selected
    // const qtyTextStyle = {
    //     color: exceeding
    //     ? muiTheme.palette.error.light
    //     : fulfilled
    //     ? muiTheme.palette.success.light
    //     : selected
    //     ? muiTheme.palette.primary.contrastText
    //     : muiTheme.palette.text.primary,
    // }    
    const qtyTextStyle = {
        color: selected
        ? muiTheme.palette.primary.contrastText
        : exceeding
        ? muiTheme.palette.error.light
        : fulfilled
        ? muiTheme.palette.success.light
        : muiTheme.palette.text.primary,
    }

    useEffect(() => {
        console.debug('==== selected', binRef, { selected, baseTextStyle })
    }, [selected])

    useEffect(() => {
        console.debug('==== exceeding', binRef, { exceeding, baseTextStyle }, muiTheme.palette.error.main)
    }, [exceeding])

    if (hideEmpty && quantity === 0) {
        return null; // <></> ? undefined ?
    }

    const binStyle : React.CSSProperties = {
        ...p.style,
        ...baseTextStyle,
        direction: isArabic(params) ? 'rtl' : 'ltr',
    }

    return (
        <>
            <div
                // FIXME use <Droppable> instead of <div>
                ref={setNodeRef}
                className={fullClass}
                onClick={(e: MouseEvent<HTMLDivElement>) => {
                    console.debug('>>> onClick', binRef, e)
                    if(!childBinRefs.length) {
                        e.stopPropagation(); // the parent bins in the tree will not be selectable; the most specific dom element is the first one to be clicked
                    }
                    onSelectBin(binRef);
                }}
                style={
                    selected || isOverFullBin
                        ? {
                              backgroundColor: baseColor,
                              borderColor: muiTheme.palette.text.primary,
                              ...binStyle,
                         } : {
                              backgroundColor:
                                  muiTheme.palette.background.default,
                              borderColor: muiTheme.palette.text.secondary,
                              ...binStyle,
                          }
                          
                }
            >
                <div
                    className={baseTextClass}
                    style={{
                        ...fluidM,
                        ...baseTextStyle,
                    }}
                >
                    {text}
                </div>
                <div
                    style={{
                        ...fluidM,
                        ...qtyTextStyle,
                        alignSelf: 'center',
                        marginTop: '0.25rem',
                        // FIXME use pills style text bg ?
                        // backgroundColor: 'white',
                        // textAlign: 'center',
                        // padding: '0 2px'
                    }}
                    className={`font-extrabold rounded`}
                >
                    {qtyStr}
                </div>
                {(isOverFullBin || isSelectedFullBin) && p.children}
            </div>
            {arrowType && <div className="flex justify-center items-center">
                {arrowType === 'down' && <Icons.ArrowDown {...iconProps} />}
                {arrowType === 'up' && <Icons.ArrowUp {...iconProps} />}
                {arrowType === 'left' && <Icons.ArrowLeft {...iconProps} />}
                {arrowType === 'right' && <Icons.ArrowRight {...iconProps} />}
            </div>}
        </>
    )
})
